import { decode as msgpackDecode } from '@msgpack/msgpack';
import { GameEvent } from '../game/GameEvents';
import { GameUpdateMessage } from '../game/Messages';
import { ObjectType, ServerMessage } from '../generated/bebop';
import { ServerMessageTag } from './BebopTags';

export type ChannelMessage =
  | {
      type: 'sync';
      objectType: 'lobby' | 'game';
      objectID?: string;
      sequenceID?: number;
      topic?: string;
    }
  | {
      type: 'update';
      objectType: 'lobby' | 'game';
      objectID?: string;
      sequenceID?: number;
      topic?: string;
      data: GameUpdateMessage | null;
    }
  | {
      type: 'chat';
      userID?: string;
      room?: string;
      message?: string;
    }
  | {
      type: 'authSuccess';
      userID: string;
    }
  | {
      type: 'authError';
      message: string;
    };

type BebopGameUpdateMessage = NonNullable<
  Extract<ReturnType<typeof ServerMessage.decode>, { tag: 1 }>['value']['data']
>;

export function decodeChannelMessage(data: ArrayBuffer): ChannelMessage | null {
  let parsed: ReturnType<typeof ServerMessage.decode>;
  try {
    parsed = ServerMessage.decode(new Uint8Array(data));
  } catch (err) {
    console.error('Failed to decode server message', err);
    return null;
  }

  switch (parsed.tag) {
    case ServerMessageTag.ObjectUpdateMessage: {
      const msg = parsed.value;
      const header = msg.header;
      if (msg.data) {
        return {
          type: 'update',
          objectType:
            header?.objectType === ObjectType.Lobby ? 'lobby' : 'game',
          objectID: header?.objectID,
          sequenceID: header?.sequenceID,
          topic: header?.topic,
          data: decodeGameUpdateMessage(msg.data),
        };
      } else {
        return {
          type: 'sync',
          objectType:
            header?.objectType === ObjectType.Lobby ? 'lobby' : 'game',
          objectID: header?.objectID,
          sequenceID: header?.sequenceID,
          topic: header?.topic,
        };
      }
    }

    case ServerMessageTag.ChatNotification: {
      const msg = parsed.value;
      return {
        type: 'chat',
        userID: msg.userID,
        room: msg.room,
        message: msg.text,
      };
    }

    case ServerMessageTag.LobbyListNotification: {
      const msg = parsed.value;
      return {
        type: 'sync',
        objectType: 'lobby',
        topic: msg.topic,
      };
    }

    case ServerMessageTag.AuthSuccess: {
      const msg = parsed.value;
      return {
        type: 'authSuccess',
        userID: msg.userID ?? '',
      };
    }

    case ServerMessageTag.AuthError: {
      const msg = parsed.value;
      return {
        type: 'authError',
        message: msg.errorMessage ?? '',
      };
    }

    default:
      console.warn('Unknown server message tag:', parsed);
      return null;
  }
}

function entriesToRecord<T>(
  entries: Array<{ userID?: string } & Record<string, any>>,
  getValue: (e: any) => T,
): Record<string, T> {
  const result: Record<string, T> = {};
  for (const entry of entries) {
    if (entry.userID) {
      result[entry.userID] = getValue(entry);
    }
  }
  return result;
}

function decodeGameUpdateMessage(
  bebop: BebopGameUpdateMessage,
): GameUpdateMessage {
  return {
    gameID: bebop.gameID ?? '',
    targetSequenceID: bebop.targetSequenceID ?? 0,
    sequenceID: bebop.sequenceID ?? 0,

    newEvents: bebop.newEventsMsgpack
      ? (msgpackDecode(bebop.newEventsMsgpack) as GameEvent[])
      : [],

    gameStartTimestamp: Number(bebop.gameStartTimestamp ?? 0),
    gameUpdateTimestamp: Number(bebop.gameUpdateTimestamp ?? 0),
    gameEndTimestamp: bebop.hasGameEndTimestamp
      ? Number(bebop.gameEndTimestamp ?? 0)
      : null,

    ratingDeltas: bebop.ratingDeltas?.length
      ? (bebop.ratingDeltas as any)
      : null,

    readyByUserID: entriesToRecord(bebop.readyByUserID ?? [], (e) => e.value),
    totalThinkingMillisByPlayerID: entriesToRecord(
      bebop.totalThinkingMillisByPlayerID ?? [],
      (e) => Number(e.value),
    ),
    phaseThinkingMillisByPlayerID: entriesToRecord(
      bebop.phaseThinkingMillisByPlayerID ?? [],
      (e) => Number(e.value),
    ),
    phaseStartTimestamp: Number(bebop.phaseStartTimestamp ?? 0),

    bidsByPlayerID: entriesToRecord(bebop.bidsByPlayerID ?? [], (e) => e.bid),
    resolvesByPlayerID: entriesToRecord(
      bebop.resolvesByPlayerID ?? [],
      (e) => e.selection,
    ),
  };
}
